import React from 'react'
import {
    Container,
    Table,
    Row,
    Col,
    Input,
    Button,
} from 'reactstrap'
import SimpleDropdown from '../view/DropDownView'
import { AlertModalModel } from './modal/AlertModalController'
const {
    getDrawingMetadata,
    updateDrawingMetadata,
    insertDrawingMetadata,
    deleteDrawingMetadataField,
    deleteDrawingMetadataRow,
    clearDrawingMetadata,
} = require('../model/request/drawingMetadata')

class DrawingMetadataViewController extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            rows: [],
            whitelist: [],
            pendingAdd: {}, // rowIndex -> { key, value }
            newSignGivenId: '',
            newPartId: '',
        }
    }

    componentDidMount() {
        this.refresh()
    }

    // "page" should already be a number (it's typed as such in the
    // whitelist), but it's a free-form model-filled JSONB value - coerce it
    // to an integer defensively rather than trusting the stored type.
    pageNumber(row) {
        const page = parseInt(row.attributes?.page, 10)
        return Number.isNaN(page) ? null : page
    }

    refresh() {
        getDrawingMetadata(this.props.id).then(
            (result) => this.setState({
                // Quote-level first. Then sign/part rows by page ascending
                // (rows with no page sort after ones that have it), and
                // alphabetically by sign_given_id/part_id as the tie-break
                // within the same page - sign types/parts can otherwise
                // switch order between refreshes.
                rows: [...result.data.metadata].sort((a, b) => {
                    if (!a.sign_given_id) return -1
                    if (!b.sign_given_id) return 1

                    const pageA = this.pageNumber(a)
                    const pageB = this.pageNumber(b)
                    if (pageA !== null && pageB !== null && pageA !== pageB) {
                        return pageA - pageB
                    }
                    if ((pageA !== null) !== (pageB !== null)) {
                        return pageA !== null ? -1 : 1
                    }

                    if (a.sign_given_id !== b.sign_given_id) {
                        return a.sign_given_id.localeCompare(b.sign_given_id)
                    }
                    if (!a.part_id) return -1
                    if (!b.part_id) return 1
                    return a.part_id.localeCompare(b.part_id)
                }),
                whitelist: result.data.whitelist,
                pendingAdd: {},
            }),
            (error) => this.props.showError(error),
        )
    }

    updateFieldValue(rowIndex, key, value) {
        const rows = this.state.rows.map((row, i) =>
            i === rowIndex ? { ...row, attributes: { ...row.attributes, [key]: value } } : row
        )
        this.setState({ rows })
    }

    // Sends only this one field, not the whole row's attributes - both the
    // Save and Verify buttons call this with the field's current value
    // (edited or not). Any human-submitted value is marked verified server
    // side, so "save an edit" and "confirm this extracted value is correct"
    // are the same write - the two buttons just make the intent explicit.
    saveField(row, key) {
        updateDrawingMetadata(
            this.props.id, row.sign_given_id, row.part_id, { [key]: row.attributes[key] }).then(
            () => this.refresh(),
            (error) => this.props.showError(error),
        )
    }

    deleteField(row, key) {
        this.props.showAlertModal(
            (new AlertModalModel())
                .setTitle('Are you sure?')
                .setMessage('The field "' + key + '" will be deleted. You can not undo this action.')
                .addYesButton((e) => {
                    deleteDrawingMetadataField(this.props.id, row.sign_given_id, row.part_id, key).then(
                        () => this.refresh(),
                        (error) => this.props.showError(error),
                    )
                    this.props.closeModal()
                })
                .addNoButton((e) => {this.props.closeModal()})
        )
    }

    deleteRow(row) {
        const label = this.rowLabel(row).toLowerCase()
        this.props.showAlertModal(
            (new AlertModalModel())
                .setTitle('Are you sure?')
                .setMessage('All attributes under ' + label + ' will be deleted. You can not undo this action.')
                .addYesButton((e) => {
                    deleteDrawingMetadataRow(this.props.id, row.sign_given_id, row.part_id).then(
                        () => this.refresh(),
                        (error) => this.props.showError(error),
                    )
                    this.props.closeModal()
                })
                .addNoButton((e) => {this.props.closeModal()})
        )
    }

    // Human-readable label for a row's section header/delete button/confirm
    // message - shared so they all describe the same row consistently.
    rowLabel(row) {
        if (!row.sign_given_id) return 'Quote-level'
        return row.part_id ? 'Sign ' + row.sign_given_id + ' - Part ' + row.part_id : 'Sign ' + row.sign_given_id
    }

    clearAll() {
        this.props.showAlertModal(
            (new AlertModalModel())
                .setTitle('Are you sure?')
                .setMessage('All metadata on this quote - the quote-level section and every sign - ' +
                    'will be deleted. You can not undo this action.')
                .addYesButton((e) => {
                    clearDrawingMetadata(this.props.id).then(
                        () => this.refresh(),
                        (error) => this.props.showError(error),
                    )
                    this.props.closeModal()
                })
                .addNoButton((e) => {this.props.closeModal()})
        )
    }

    addSign() {
        const signGivenId = this.state.newSignGivenId.trim()
        if (!signGivenId) return
        const partId = this.state.newPartId.trim() || null
        insertDrawingMetadata(this.props.id, signGivenId, partId, {}).then(
            () => this.setState({ newSignGivenId: '', newPartId: '' }, () => this.refresh()),
            (error) => this.props.showError(error),
        )
    }

    availableKeysForRow(row) {
        const isSignRow = !!row.sign_given_id
        const existingKeys = Object.keys(row.attributes || {})
        return this.state.whitelist
            .filter((a) => !!a.per_sign === isSignRow)
            .filter((a) => !existingKeys.includes(a.key))
            .map((a) => a.key)
    }

    setPendingAdd(rowIndex, field, value) {
        this.setState({
            pendingAdd: {
                ...this.state.pendingAdd,
                [rowIndex]: { ...(this.state.pendingAdd[rowIndex] || {}), [field]: value },
            },
        })
    }

    addField(rowIndex, row) {
        const pending = this.state.pendingAdd[rowIndex] || {}
        if (!pending.key) return
        this.updateFieldValue(rowIndex, pending.key, pending.value || '')
        this.setState({
            pendingAdd: { ...this.state.pendingAdd, [rowIndex]: {} },
        })
    }

    renderAddFieldControl(row, rowIndex) {
        const availableKeys = this.availableKeysForRow(row)
        if (availableKeys.length === 0) {
            return null
        }
        const pending = this.state.pendingAdd[rowIndex] || {}
        return (
            <Row style={{ marginTop: '10px' }}>
                <Col md="3">
                    <SimpleDropdown
                        index={availableKeys.indexOf(pending.key)}
                        values={availableKeys}
                        onChange={(i) => this.setPendingAdd(rowIndex, 'key', availableKeys[i])}
                    />
                </Col>
                <Col md="3">
                    <Input
                        placeholder="Value"
                        value={pending.value || ''}
                        onChange={(e) => this.setPendingAdd(rowIndex, 'value', e.target.value)}
                    />
                </Col>
                <Col>
                    <Button color="success" onClick={() => this.addField(rowIndex, row)}>Add Field</Button>
                </Col>
            </Row>
        )
    }

    renderRow(row, rowIndex) {
        const keys = Object.keys(row.attributes || {})
        return (
            <div key={(row.sign_given_id || 'quote-level') + '/' + (row.part_id || '')}
                style={{ marginBottom: '30px' }}>
                <Row>
                    <Col>
                        <h5>{this.rowLabel(row)}</h5>
                    </Col>
                    <Col xs="auto">
                        <Button color="danger" onClick={() => this.deleteRow(row)}>
                            {row.sign_given_id ? 'Delete All Sign Type' : 'Delete All Quote-level'}
                        </Button>
                    </Col>
                </Row>
                <Row><Col><div style={{'overflowX': 'auto'}}><Table>
                    <thead>
                        <tr>
                            <th>Field</th>
                            <th>Value</th>
                            <th style={{ width: '100px' }}>Confidence</th>
                            <th style={{ width: '120px' }}>Verified</th>
                            <th style={{ width: '90px' }}>Save</th>
                            <th style={{ width: '90px' }}>Verify</th>
                            <th style={{ width: '90px' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((key) => {
                            const meta = (row.attributes_meta || {})[key] || {}
                            return (
                                <tr key={key}>
                                    <td>{key}</td>
                                    <td>
                                        <Input
                                            value={row.attributes[key] ?? ''}
                                            onChange={(e) => this.updateFieldValue(
                                                rowIndex, key, e.target.value)}
                                        />
                                    </td>
                                    <td>{meta.confidence != null ? meta.confidence : '-'}</td>
                                    <td>{meta.verified_by ? meta.verified_by : (meta.needs_review ? 'Needs review' : '-')}</td>
                                    <td>
                                        <Button color="primary" onClick={() => this.saveField(row, key)}>Save</Button>
                                    </td>
                                    <td>
                                        <Button color="success" onClick={() => this.saveField(row, key)}>Verify</Button>
                                    </td>
                                    <td>
                                        <Button color="danger" onClick={() => this.deleteField(row, key)}>Delete</Button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table></div></Col></Row>
                {this.renderAddFieldControl(row, rowIndex)}
            </div>
        )
    }

    render() {
        return (
            <Container>
                {this.props.breadCrumbs(5, this.props.id)}
                <h3 style={{ marginTop: '20px', marginBottom: '20px' }}>Metadata</h3>
                <p>Attributes extracted from this quote's uploaded drawings.</p>
                {this.state.rows.length === 0 ?
                    <p>No extracted metadata yet for this quote.</p> :
                    this.state.rows.map((row, i) => this.renderRow(row, i))}
                <Row style={{ marginTop: '20px' }}>
                    <Col md="3">
                        <Input
                            placeholder="Sign type (e.g. SV)"
                            value={this.state.newSignGivenId}
                            onChange={(e) => this.setState({ newSignGivenId: e.target.value })}
                        />
                    </Col>
                    <Col md="3">
                        <Input
                            placeholder="Part ID (optional)"
                            value={this.state.newPartId}
                            onChange={(e) => this.setState({ newPartId: e.target.value })}
                        />
                    </Col>
                    <Col>
                        <Button color="success" onClick={() => this.addSign()}>
                            {this.state.newPartId.trim() ? 'Add Part' : 'Add Sign'}
                        </Button>
                    </Col>
                </Row>
                {this.state.rows.length > 0 &&
                    <Row style={{ marginTop: '20px' }}>
                        <Col xs="auto">
                            <Button color="danger" onClick={() => this.clearAll()}>Delete All</Button>
                        </Col>
                    </Row>}
            </Container>
        )
    }
}

export default DrawingMetadataViewController
